import React, { type FC, type KeyboardEvent } from "react";
import { useTranslation } from "@edreamsodigeo/frontend-core";
import {
  Field,
  InnerLabel,
  Input,
  useInputField,
  ValidationErrorMessage,
} from "@edreamsodigeo/prisma-design-system";

import { KeyCodes } from "../../../../utils";
import type { EmailErrorBody, EmailInputProps } from "./contract";
import { EmailFormatErrorMessageBody } from "./EmailErrorMessageBody";

export const EmailInput: FC<EmailInputProps> = ({
  id,
  email,
  setEmail,
  errors,
  clearErrors,
  disabled,
  onEnter,
  onClick,
  label,
  trackingPrefix,
  CustomErrorComponent,
}) => {
  const { t } = useTranslation();
  const error = !!errors.length;
  const { fieldProps, labelProps, inputProps } = useInputField({
    id,
    value: email,
    onChange({ currentTarget }) {
      const value = currentTarget.value;
      setEmail(value);
      if (error) {
        clearErrors();
      }
    },
    validate: () => !error,
    onReset: () => {},
  });

  const onKeyDownHandler = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (KeyCodes.Enter(event)) {
      onEnter?.();
    }
  };
  const hasCustomErrorComponent = !!CustomErrorComponent;

  return (
    <>
      <Field {...fieldProps}>
        <InnerLabel {...labelProps}>
          {label ? label : t("auth.form.email.label")}
        </InnerLabel>
        <Input
          {...inputProps}
          type="email"
          invalid={!!error}
          disabled={disabled}
          onKeyDown={onKeyDownHandler}
          onClick={onClick}
        />
      </Field>
      <ValidationErrorMessage htmlFor={id} invalid={!!error}>
        {errors.map((err, i) =>
          hasCustomErrorComponent ? (
            <CustomErrorComponent
              key={i}
              error={err}
              trackingPrefix={trackingPrefix}
            />
          ) : (
            <DefaultErrorComponent
              key={i}
              error={err}
              trackingPrefix={trackingPrefix}
            />
          ),
        )}
      </ValidationErrorMessage>
    </>
  );
};

const DefaultErrorComponent: FC<EmailErrorBody> = ({
  key,
  error,
  trackingPrefix,
}) => (
  <EmailFormatErrorMessageBody
    key={key}
    error={error}
    trackingPrefix={trackingPrefix}
  />
);
